import { Code, Database, Cog, Zap, Palette } from "lucide-react"
import { Button } from "@/components/ui/je-button"
import { useServiceSelection } from "@/components/ServiceSelectionProvider"
import { useLanguage } from "@/contexts/LanguageContext"

const ServicesSection = () => {
  const { addService } = useServiceSelection()
  const { t } = useLanguage()

  const scrollToContact = () => {
    const contactElement = document.getElementById('contact');
    if (contactElement) {
      const headerHeight = 80; // Account for sticky header
      const elementPosition = contactElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  const handleServiceRequest = (serviceName: string) => {
    addService(serviceName.toLowerCase().replace(/\s+/g, '-'), serviceName);
    scrollToContact();
  }
  const services = [
    {
      icon: Code,
      title: t('services.computer'),
      description: t('services.computer.desc'),
      gradient: "from-je-red to-eth-purple"
    },
    {
      icon: Database,
      title: t('services.agronomy'),
      description: t('services.agronomy.desc'),
      gradient: "from-eth-green to-eth-blue"
    },
    {
      icon: Cog,
      title: t('services.mechanical'), 
      description: t('services.mechanical.desc'),
      gradient: "from-eth-purple to-je-red"
    },
    {
      icon: Zap,
      title: t('services.microtechnics'),
      description: t('services.microtechnics.desc'),
      gradient: "from-je-red to-eth-green"
    },
    {
      icon: Palette,
      title: t('services.architecture'),
      description: t('services.architecture.desc'),
      gradient: "from-eth-blue to-eth-purple"
    },
    {
      icon: Code,
      title: t('services.civil'),
      description: t('services.civil.desc'),
      gradient: "from-je-red to-eth-green"
    },
    {
      icon: Database,
      title: t('services.nature'),
      description: t('services.nature.desc'),
      gradient: "from-eth-green to-je-red"
    },
    {
      icon: Cog,
      title: t('services.landscape'),
      description: t('services.landscape.desc'),
      gradient: "from-eth-purple to-eth-blue"
    },
    {
      icon: Zap,
      title: t('services.building'),
      description: t('services.building.desc'),
      gradient: "from-eth-blue to-je-red"
    }
  ]

  return (
    <section id="services" className="py-24 bg-je-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="text-section-title text-je-black mb-6">
            {t('services.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('services.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="card-modern p-8 group scroll-reveal animate-bounce-soft"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Icon avec gradient background */}
              <div className="mb-6 relative">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} p-4 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <service.icon className="h-full w-full text-white" />
                </div>
              </div>
              
              <h3 className="text-card-title text-je-black mb-4 font-bold">
                {service.title}
              </h3>
              
              <p className="text-muted-foreground mb-6 leading-relaxed text-sm">
                {service.description}
              </p>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full btn-modern group-hover:bg-je-red group-hover:text-je-white group-hover:border-je-red focus:outline-none focus:ring-2 focus:ring-je-red/50"
                onClick={() => handleServiceRequest(service.title)}
              >
                {t('services.request')}
              </Button>

              {/* Decorative element */}
              <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-accent opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ServicesSection